import { Component, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import _ from 'lodash';

import { EmojiPickerComponent } from 'src/app/components/emoji-picker/emoji-picker.component';
import { BOT_HANDLING, REPLY_TIMEOUT_MINUTES_DEFAULT, TenantSlackInboxConfig } from 'src/app/constants';
import { environment } from 'src/environments/environment';
import { INTAKE_MODES, INTAKE_MODE_LABELS, IntakeMode, AGENT_HANDOFF_MODES, IntakeSettings } from 'src/models/channels';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-admin-slack',
  templateUrl: './admin-slack.component.html',
  styleUrls: ['./admin-slack.component.scss'],
})
export class AdminSlackComponent implements OnInit {
  initialized = false;
  slackEnabled: boolean;
  replyTimeoutMinutes?: number;
  enableAutomatedTicketing: boolean;
  assignToTaggedUserEnabled: boolean;
  assignToFirstResponderEnabled: boolean;
  slackAwaySettings: any;
  hasReadDndScope: boolean;
  advancedInternalTicketingEnabled: boolean;
  advancedInternalTicketingDefaultCustomerId: string;
  advancedInternalTicketingDefaultCustomer: any;
  slackInboxConfig: TenantSlackInboxConfig;
  connectedTeams: any[];
  primaryTeam: any;

  defaultChannelIntakeSettings: IntakeSettings;
  botDmIntakeMode: IntakeMode;
  isBotEnabled: boolean;
  isLoading = false;
  customTokens: Record<string, any> = {};

  INTAKE_MODES = INTAKE_MODES;
  INTAKE_MODE_LABELS = INTAKE_MODE_LABELS;
  AGENT_HANDOFF_MODES = AGENT_HANDOFF_MODES;

  constructor(
    public authService: AuthService,
    private slackService: SlackService,
    private msgService: MessageService,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private customersService: CustomersService,
  ) {}

  async ngOnInit() {
    this.load();

    this.hasReadDndScope = await this.slackService.hasScope('dnd:read');

    // check if there is an oauth error
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('oauth_error');

    if (oauthError) {
      this.msgService.show(`Something went wrong with Slack OAuth: ${oauthError}`);
    }
  }

  async load() {
    this.isLoading = true;

    const tenant = this.authService.tenant;

    this.replyTimeoutMinutes = tenant?.replyTimeoutMinutes ?? REPLY_TIMEOUT_MINUTES_DEFAULT;
    this.enableAutomatedTicketing = !tenant?.disableAutomatedTicketing;
    this.assignToTaggedUserEnabled = tenant?.assignToTaggedUserEnabled ?? true;
    this.assignToFirstResponderEnabled = tenant?.assignToFirstResponderEnabled ?? true;
    this.slackAwaySettings = tenant?.slackAwaySettings;
    this.advancedInternalTicketingEnabled = tenant?.advancedInternalTicketingEnabled;
    this.advancedInternalTicketingDefaultCustomerId = tenant?.advancedInternalTicketingDefaultCustomerId;
    this.defaultChannelIntakeSettings = tenant?.defaultChannelIntakeSettings ?? {
      bot: {
        enabled: false,
        maxResponses: null,
        handoffMode: AGENT_HANDOFF_MODES.PROMPT,
      },
    };
    this.slackInboxConfig = tenant?.slackInboxConfig ?? {};
    this.botDmIntakeMode = tenant?.botDmIntakeMode ?? INTAKE_MODES.BOT_TRIAGE;
    this.isBotEnabled = _.get(tenant, 'botHandling', BOT_HANDLING.OFF) === BOT_HANDLING.ALL;
    this.customTokens = _.cloneDeep(tenant.customTokens || {});

    this.initialized = true;
    this.slackEnabled = this.authService.integrations.slackEnabled;

    this.connectedTeams = await this.slackService.getSlackConnections();
    this.primaryTeam = this.connectedTeams.find((team) => team.isPrimary);

    if (this.advancedInternalTicketingDefaultCustomerId) {
      try {
        this.advancedInternalTicketingDefaultCustomer = await this.customersService.get(
          this.advancedInternalTicketingDefaultCustomerId,
        );
      } catch (e) {
        console.warn('Default customer was not found');
      }
    }

    try {
      // Test to see if we can get channels from Slack
      const _triageChannel = await this.slackService.getChannel(this.authService.tenant.defaultTriageChannelId);
    } catch (e) {
      if (e.status === 500) {
        this.slackEnabled = false;
      }
    }

    this.isLoading = false;
  }

  async save() {
    if (!this.initialized) {
      return;
    }

    const updates: any = {
      botHandling: this.isBotEnabled ? BOT_HANDLING.ALL : BOT_HANDLING.OFF,
      replyTimeoutMinutes: this.replyTimeoutMinutes,
      disableAutomatedTicketing: !this.enableAutomatedTicketing,
      assignToTaggedUserEnabled: this.assignToTaggedUserEnabled,
      assignToFirstResponderEnabled: this.assignToFirstResponderEnabled,
      slackAwaySettings: this.slackAwaySettings,
      slackInboxConfig: this.slackInboxConfig,
      botDmIntakeMode: this.botDmIntakeMode,
      defaultChannelIntakeSettings: this.defaultChannelIntakeSettings,
      customTokens: this.customTokens,
    };

    if (this.advancedInternalTicketingEnabled !== undefined) {
      updates.advancedInternalTicketingEnabled = this.advancedInternalTicketingEnabled;
      updates.advancedInternalTicketingDefaultCustomerId = this.advancedInternalTicketingDefaultCustomerId;

      if (
        !this.authService.tenant.accountsEnabled &&
        updates.advancedInternalTicketingEnabled &&
        !updates.advancedInternalTicketingDefaultCustomerId
      ) {
        this.msgService.showBottom('Please select a default customer for advanced internal ticketing');
        return;
      }
    }

    try {
      await this.authService.updateTenant(updates);

      this.load();
      this.msgService.showBottom('✔︎ Saved');
    } catch (err) {
      this.msgService.showBottom(`Something went wrong: ${err.message}`);
      this.initialized = true;
    }
  }

  connectSlack(isSecondary = false, teamId?: string) {
    this.slackService.oauth(window.location.href, window.location.origin, isSecondary, teamId);
  }

  async disconnectSlack(slackTeamId: string) {
    if (!confirm('Are you sure you want to disconnect this Slack workspace?')) {
      return;
    }

    await this.slackService.disconnect(slackTeamId);

    window.location.reload();
  }

  openSlack() {
    window.open(`https://slack.com/app_redirect?app=${environment.slack.appID}`);
  }

  deleteAwayEmoji(i: number) {
    this.slackAwaySettings.awayEmojis.splice(i, 1);
  }

  async openEmojiPicker(action: string, _event) {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();

          // Handle native and custom emojis
          const value = emoji.native ?? `:${emoji.id}:`;

          if (!action) {
            this.slackAwaySettings.awayEmojis.push(value);
            return;
          }
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  async showCustomerOptions() {
    this.searchablePopoverService.customers({
      event,
      selectedCustomerId: this.advancedInternalTicketingDefaultCustomerId,
      callback: (id: string, _customerName: string, customer: any) => {
        this.advancedInternalTicketingDefaultCustomerId = id;
        this.advancedInternalTicketingDefaultCustomer = customer;
      },
    });
  }

  onIntakeSettingsChange(settings: IntakeSettings) {
    this.defaultChannelIntakeSettings = settings;
  }

  onModalSubtitleChange(event: string | null) {
    this.customTokens['CREATE_CONVERSATION_MODAL_SUBTITLE'] = event;
  }
}
